<template>
  <div>
    <FullCalendar ref="fullCalendar" :options="calendarOptionsWithEvents"/>

    <!-- View Calendar Event Modals -->
    <modal-before-container>
      <modal-default
        ref="taskViewModal"
        title="Task"
        @close="closeModal"
        is-wide
      >
        <up-next-task-item :store-task="task" @edit="openTaskModal"/>
      </modal-default>

      <modal-default
        ref="appointmentViewModal"
        title="Appointment"
        @close="closeModal"
        is-wide
      >
        <up-next-appointment-item :appointment="appointment"/>
      </modal-default>
    </modal-before-container>

    <!-- Task Modal -->
    <modal-container>
      <modal-crud-form
        ref="taskModal"
        title="Task"
        form-component="task-form"
        is-double-column
      />
    </modal-container>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list';
import dates from "../../services/dates";
import { MODELS } from "../../services/models";
import UpNextTaskItem from "./components/UpNextTaskItem.vue";
import UpNextAppointmentItem from "./components/UpNextAppointmentItem.vue";

export default {
  name: "UpNextCalendarDesktop",
  components: {
    UpNextAppointmentItem,
    UpNextTaskItem,
    FullCalendar,
  },
  data() {
    return {
      task: null,
      appointment: null,
      calendarOptions: {
        plugins: [dayGridPlugin, interactionPlugin, listPlugin],
        initialView: 'dayGridMonth',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridDay,dayGridWeek,dayGridMonth,listWeek'
        },
        buttonText: {
          today: 'Today',
          month: 'Month',
          week: 'Week',
          day: 'Day',
          list: 'List'
        },
        datesSet: this.handleDatesSet,
        eventClick: this.handleEventClick
      }
    }
  },
  computed: {
    tasks() {
      return this.$store.getters["upNext/incompleteTasks"].map(task => {
        return {
          id: task.id,
          title: task.label,
          start: task.due_at,
          extendedProps: task,
          color: 'grey',
          className: (task.status === 2 || task.status === 3) ? "event-completed" : ""
        }
      })
    },
    appointments() {
      return this.$store.getters["upNext/pendingAppointments"].map(appointment => {
        return {
          id: appointment.id,
          title: appointment.title,
          start: appointment.start_at,
          end: appointment.end_at,
          extendedProps: appointment,
          color: '#F25430'
        }
      });
    },
    tasksAndAppointments() {
      return [...this.tasks, ...this.appointments];
    },
    calendarOptionsWithEvents() {
      return {
        ...this.calendarOptions,
        events: this.tasksAndAppointments
      };
    },
  },
  methods: {
    calendarApi() {
      return this.$refs.fullCalendar.getApi();
    },
    getDates() {
      const calendarView = this.calendarApi().view;
      return {
        start_at: dates.convertJSDateToISO(calendarView.activeStart),
        end_at: dates.convertJSDateToISO(calendarView.activeEnd),
      };
    },
    handleControls(event) {
      const calendarApi = this.calendarApi();

      if (event === 'today') calendarApi.today();
      else if (event === 'prev') calendarApi.prev();
      else if (event === 'next') calendarApi.next();
    },
    handleDatesSet(info) {
      this.$emit('setDates', {
        start_at: info.startStr,
        end_at: info.endStr
      });
    },
    handleEventClick(info) {
      info.jsEvent.preventDefault();
      const event = info.event;
      if (event.extendedProps.class_name === MODELS.Task.class_name) {
        this.task = event.extendedProps;
        this.$refs.taskViewModal.open();
      }

      if (event.extendedProps.class_name === MODELS.Appointment.class_name) {
        this.appointment = event.extendedProps;
        this.$refs.appointmentViewModal.open();
      }
    },
    openTaskModal(task) {
      this.$refs.taskModal.open(task);
    },
    closeModal() {
      this.task = null;
      this.appointment = null;
    },
  }
}
</script>

<style scoped lang="scss">
@import "../../styles/variables/variables";

:deep.fc {
  .fc-toolbar-title  {
    padding: 0 0.75rem;
    color: rgba(55, 59, 60, 0.7);
    font-weight: bold;
    font-size: 20px;
    line-height: 30px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .fc-button-primary {
    padding: 0.4em 0.75em;
    border-radius: 2px;
    background-color: $orange;
    border-color: transparent;
    color: #fff;
    font-size: 0.85rem;

    &.fc-button-active, &:hover {
      background-color: $button-active;
      border-color: transparent;
    }
  }

  .fc-daygrid-body .fc-event {
    color: $text;
  }

  .fc-list .fc-event {
    font-size: 0.85rem;
  }

  .fc-col-header-cell, .fc-list-day {
    a {
      padding: 6px 4px;
      font-size: 0.875rem;
      font-weight: bold;
      color: $dark-grey;
      cursor: text;
    }

    &.fc-day-today .fc-scrollgrid-sync-inner, &.fc-day-today .fc-list-day-cushion {
      background: $button-active;

      a {
        color: #fff;
      }
    }
  }

  .fc-list-day-cushion a {
    padding: 0;
  }

  .fc-daygrid-day {
    .fc-daygrid-day-top a {
      color: $dark-grey;
      font-size: 0.875rem;
      cursor: text;
    }

    &.fc-day-today {
      background-color: transparent;

      .fc-daygrid-day-top a {
        background-color: $button-active;
        color: #fff;
        border-radius: 50%;
        padding: 4px 6px;
      }
    }
  }

  .event-completed {
    text-decoration: line-through;
  }
}
</style>
